import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, TrendingUp, BarChart3, Shield, CheckCircle2, ArrowRight } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const SVC_ICONS = { "Capital Raising": TrendingUp, "Asset Management": BarChart3, "Strategic Advisory": Shield };
const SLUG_TO_KEY = { "capital-raising": "capital_raising", "asset-management": "asset_management", "strategic-advisory": "strategic_advisory" };

export function ServicesOverview() {
  const { getPage } = useContent();
  const s = getPage("services", {});

  const items = ["capital_raising", "asset_management", "strategic_advisory"].map(key => {
    const svc = s[key] || {};
    const slug = key.replace(/_/g, "-");
    return { ...svc, slug, key };
  });

  return (
    <div data-testid="services-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{s.hero_label || "Our Services"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">{s.hero_headline || "Comprehensive financial solutions for growth enterprises."}</h1>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="space-y-0 border border-gray-200">
            {items.map((svc, i) => {
              const Icon = SVC_ICONS[svc.title] || TrendingUp;
              return (
                <Link key={svc.slug} to={`/services/${svc.slug}`} data-testid={`service-link-${svc.slug}`} className={`group flex flex-col md:flex-row md:items-center gap-6 p-10 md:p-12 hover:bg-navy-900 transition-all duration-500 ${i < items.length - 1 ? 'border-b border-gray-200' : ''}`}>
                  <Icon className="w-10 h-10 text-gold-500 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl md:text-3xl text-navy-900 group-hover:text-white transition-colors mb-2">{svc.title}</h3>
                    <p className="text-gray-500 group-hover:text-white/60 font-sans text-sm leading-relaxed transition-colors">{svc.description}</p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-gray-300 group-hover:text-gold-500 transition-colors flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const { getPage } = useContent();
  const s = getPage("services", {});
  const svcKey = SLUG_TO_KEY[slug];
  const svc = svcKey ? s[svcKey] : null;

  if (!svc) {
    return <div className="pt-32 pb-20 text-center"><h1 className="font-serif text-4xl text-navy-900">Service not found</h1><Link to="/services" className="text-gold-500 font-sans mt-4 inline-block">Back to Services</Link></div>;
  }

  const Icon = SVC_ICONS[svc.title] || TrendingUp;

  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/50 hover:text-white font-sans text-sm mb-6 transition-colors"><ArrowRight className="w-4 h-4 rotate-180" /> All Services</Link>
          <Icon className="w-12 h-12 text-gold-500 mb-6" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl mb-4">{svc.title}</h1>
          <p className="text-white/60 font-sans text-base md:text-lg max-w-2xl leading-relaxed">{svc.subtitle}</p>
        </div>
      </section>
      <section className="py-24 md:py-32"><div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16"><div className="max-w-3xl"><p className="text-gray-600 font-sans text-base md:text-lg leading-relaxed">{svc.description}</p></div></div></section>
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">What We Offer</p>
          <h2 className="font-serif text-4xl text-navy-900 font-light tracking-tight mb-12">Service Offerings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200">
            {(svc.offerings || []).map((o, i) => <div key={i} className={`p-8 md:p-10 bg-white ${i < 2 ? 'border-b border-gray-200' : ''} ${i % 2 === 0 ? 'md:border-r border-gray-200' : ''}`}><h3 className="font-serif text-xl text-navy-900 mb-3">{o.title}</h3><p className="text-gray-500 font-sans text-sm leading-relaxed">{o.desc}</p></div>)}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">Our Approach</p>
          <h2 className="font-serif text-4xl text-navy-900 font-light tracking-tight mb-12">Process Flow</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(svc.process || []).map(p => <div key={p.step} className="relative"><span className="font-serif text-5xl text-gray-100 absolute -top-2 left-0">{p.step}</span><div className="pt-12"><h3 className="font-serif text-xl text-navy-900 mb-2">{p.title}</h3><p className="text-gray-500 font-sans text-sm leading-relaxed">{p.desc}</p></div></div>)}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">Applications</p>
          <h2 className="font-serif text-4xl text-navy-900 font-light tracking-tight mb-12">Use Cases</h2>
          <div className="space-y-4 max-w-2xl">
            {(svc.use_cases || []).map(uc => <div key={uc} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} /><span className="font-sans text-gray-700">{uc}</span></div>)}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-navy-900 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight mb-6">Ready to get started?</h2>
          <p className="text-white/60 font-sans mb-10 max-w-xl mx-auto">Let's discuss how our {(svc.title || "").toLowerCase()} capabilities can help your business.</p>
          <Link to="/contact" data-testid="service-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">Book a Consultation <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
